import axios from 'axios';
import { Logger } from '@elyx-code/common-ts-utils';
import { popupNotification } from '../../popup-notification';
import {
	addUploadRecord,
	deleteBatchUploads,
	getAllBucketUploads,
	getPendingUploads,
	IUploadRecord,
	removeUploadRecord,
	updateUploadRecord
} from '../local-relational-database/uploads_idb';

export interface IUploadOptions {
	file: File;
	key: string;
	bucket: string;
	batchId?: string;
	getAccessToken: () => Promise<string>;
	envConfig: any;
	record?: IUploadRecord;
	onProgress?: (uploadedPartsCount: number, totalParts: number) => void;
	onComplete?: () => void;
	onError?: (error: any) => void;
}

export interface IActiveUploadState {
	id: string;
	batchId?: string;
	bucket: string;
	key: string;
	fileName: string;
	fileSize: number;
	completedParts: number;
	totalParts: number;
	status: 'uploading' | 'completed' | 'failed';
	isResumed?: boolean;
	uploadId?: string;
}

export class UploadManagerService {
	private static activeUploadsMap = new Map<string, IActiveUploadState>();
	private static abortControllers = new Map<string, AbortController>();
	private static activeRecordIds = new Map<string, number>();
	private static listeners = new Set<() => void>();

	static subscribe(listener: () => void): () => void {
		UploadManagerService.listeners.add(listener);
		return () => {
			UploadManagerService.listeners.delete(listener);
		};
	}

	private static notifyListeners() {
		for (const listener of UploadManagerService.listeners) {
			try {
				listener();
			} catch (e) {
				Logger.error('Error in UploadManagerService listener', e);
			}
		}
	}

	static getActiveUploadsForBucket(bucket: string): IActiveUploadState[] {
		const list: IActiveUploadState[] = [];
		for (const upload of UploadManagerService.activeUploadsMap.values()) {
			if (upload.bucket === bucket && upload.status === 'uploading') {
				list.push(upload);
			}
		}
		return list;
	}

	static async cancelUpload(
		uploadKey: string,
		getAccessToken?: () => Promise<string>,
		envConfig?: any
	) {
		const controller = UploadManagerService.abortControllers.get(uploadKey);
		if (controller) {
			controller.abort();
			UploadManagerService.abortControllers.delete(uploadKey);
		}

		const activeState =
			UploadManagerService.activeUploadsMap.get(uploadKey);
		const recordId = UploadManagerService.activeRecordIds.get(uploadKey);

		if (activeState?.batchId) {
			await deleteBatchUploads(activeState.batchId);
		} else if (recordId) {
			await removeUploadRecord(recordId);
			UploadManagerService.activeRecordIds.delete(uploadKey);
		}

		if (activeState && getAccessToken && envConfig) {
			try {
				const headers = {
					Authorization: `Bearer ${await getAccessToken()}`
				};

				if (activeState.uploadId) {
					await axios.delete(
						`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/multipart/${activeState.uploadId}?bucket=${activeState.bucket}&key=${encodeURIComponent(activeState.key)}`,
						{ headers }
					);
				}

				await axios.delete(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files`,
					{
						headers,
						data: {
							bucket: activeState.bucket,
							keys: [activeState.key]
						}
					}
				);
			} catch (e) {
				// Ignore if file or multipart session was already cleaned up
			}
		}

		UploadManagerService.activeUploadsMap.delete(uploadKey);
		UploadManagerService.notifyListeners();
	}

	static async resumePendingUploadsForProject(
		_projectId: string,
		getAccessToken: () => Promise<string>,
		envConfig: any
	) {
		return this.resumePendingUploadsForBucket(
			undefined,
			getAccessToken,
			envConfig
		);
	}

	static async resumePendingUploadsForBucket(
		bucket: string | undefined,
		getAccessToken: () => Promise<string>,
		envConfig: any
	) {
		const allRecords = await getAllBucketUploads(bucket);
		if (allRecords.length === 0) return;

		const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
		const now = Date.now();

		const pending: IUploadRecord[] = [];
		const completed: IUploadRecord[] = [];

		for (const record of allRecords) {
			const uploadTimestamp =
				record.createdAt || record.updatedAt || record.lastModified;
			if (uploadTimestamp && now - uploadTimestamp > THREE_DAYS_MS) {
				Logger.log(
					`Purging expired upload record (>3 days old): ${record.key}`
				);
				if (record.id) await removeUploadRecord(record.id);
				continue;
			}

			if (record.status === 'completed') {
				completed.push(record);
			} else if (
				record.file &&
				((record.file as any) instanceof File ||
					(record.file as any) instanceof Blob)
			) {
				pending.push(record);
			} else {
				if (record.id) await removeUploadRecord(record.id);
			}
		}

		if (pending.length === 0) {
			// All uploads finished, purge completed markers
			for (const c of completed) {
				if (c.id) await removeUploadRecord(c.id);
			}
			return;
		}

		// Fire ONE single batch notification
		const totalBatchCount = pending.length + completed.length;
		if (totalBatchCount > 1) {
			popupNotification({
				text: `Resuming upload batch (${completed.length} of ${totalBatchCount} files completed)...`,
				color: 'success'
			});
		} else if (pending[0]) {
			const fileName = pending[0].key.split('/').pop() || 'unknown';
			popupNotification({
				text: `Resuming upload for "${fileName}"...`,
				color: 'success'
			});
		}

		Logger.log(
			`Found ${pending.length} pending uploads (${completed.length} completed) to resume in background.`
		);

		for (const record of pending) {
			const fileName = record.key.split('/').pop() || 'unknown';
			const fileObj =
				(record.file as any) instanceof File
					? record.file
					: new File([record.file as any], fileName, {
							type: (record.file as any).type
						});

			this.uploadFile({
				file: fileObj,
				key: record.key,
				bucket: record.bucket,
				batchId: record.batchId,
				getAccessToken,
				envConfig,
				record
			}).catch((err) => {
				Logger.error('Failed to resume upload for ' + record.key, err);
			});
		}
	}

	static async uploadFile(options: IUploadOptions) {
		const {
			file,
			key,
			bucket,
			batchId,
			getAccessToken,
			envConfig,
			record
		} = options;

		let activeRecordId = record?.id;
		let uploadId = record?.uploadId;
		const activeBatchId = batchId || record?.batchId;
		const uploadedParts = [...(record?.parts || [])];

		const fileName = file.name || key.split('/').pop() || 'file';
		const isMultipart = file.size > 5 * 1024 * 1024;
		const chunkSize = 5 * 1024 * 1024; // 5MB
		const numParts = isMultipart ? Math.ceil(file.size / chunkSize) : 1;
		const uploadKey = `${bucket}:${key}`;

		const controller = new AbortController();
		UploadManagerService.abortControllers.set(uploadKey, controller);

		UploadManagerService.activeUploadsMap.set(uploadKey, {
			id: uploadKey,
			batchId: activeBatchId,
			bucket,
			key,
			fileName,
			fileSize: file.size,
			completedParts: uploadedParts.length,
			totalParts: numParts,
			status: 'uploading',
			isResumed: !!record,
			uploadId
		});
		UploadManagerService.notifyListeners();

		const dbInsertionPromise = !activeRecordId
			? addUploadRecord({
					batchId: activeBatchId,
					bucket,
					key,
					file,
					parts: [],
					status: 'uploading',
					createdAt: Date.now(),
					updatedAt: Date.now()
				}).then((rec) => {
					activeRecordId = rec.id;
					if (rec.id) {
						UploadManagerService.activeRecordIds.set(
							uploadKey,
							rec.id
						);
					}
					return rec;
				})
			: Promise.resolve(record);

		if (activeRecordId) {
			UploadManagerService.activeRecordIds.set(uploadKey, activeRecordId);
		}

		try {
			if (isMultipart) {
				if (!uploadId) {
					const createRes = await axios.post(
						`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/multipart?bucket=${bucket}&key=${encodeURIComponent(key)}&contentType=${encodeURIComponent(file.type || 'application/octet-stream')}`,
						{},
						{
							headers: {
								Authorization: `Bearer ${await getAccessToken()}`
							},
							signal: controller.signal
						}
					);
					uploadId = createRes.data.uploadId as string;

					await dbInsertionPromise;
					if (activeRecordId) {
						await updateUploadRecord(activeRecordId, { uploadId });
					}
				}

				for (let part = 1; part <= numParts; part++) {
					if (controller.signal.aborted) {
						throw new axios.Cancel('Upload cancelled by user');
					}

					if (uploadedParts.some((p) => p.PartNumber === part)) {
						continue;
					}

					const start = (part - 1) * chunkSize;
					const end = Math.min(start + chunkSize, file.size);
					const chunk = file.slice(start, end);

					const partRes = await axios.post(
						`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/multipart/${uploadId}/part?bucket=${bucket}&key=${encodeURIComponent(key)}&partNumber=${part}`,
						{},
						{
							headers: {
								Authorization: `Bearer ${await getAccessToken()}`
							},
							signal: controller.signal
						}
					);

					const uploadPartResponse = await axios.put(
						partRes.data.uploadUrl,
						chunk,
						{
							headers: {
								'Content-Type': 'application/octet-stream'
							},
							signal: controller.signal
						}
					);

					const etag = uploadPartResponse.headers['etag'];
					if (!etag) throw new Error('Missing ETag header');

					uploadedParts.push({
						ETag: etag.replace(/"/g, ''),
						PartNumber: part
					});

					await dbInsertionPromise;
					if (activeRecordId) {
						await updateUploadRecord(activeRecordId, {
							parts: uploadedParts
						});
					}

					UploadManagerService.activeUploadsMap.set(uploadKey, {
						id: uploadKey,
						batchId: activeBatchId,
						bucket,
						key,
						fileName,
						fileSize: file.size,
						completedParts: uploadedParts.length,
						totalParts: numParts,
						status: 'uploading',
						isResumed: !!record,
						uploadId
					});
					UploadManagerService.notifyListeners();

					if (options.onProgress) {
						options.onProgress(uploadedParts.length, numParts);
					}
				}

				await axios.post(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/multipart/${uploadId}/complete?bucket=${bucket}&key=${encodeURIComponent(key)}`,
					{ parts: uploadedParts },
					{
						headers: {
							Authorization: `Bearer ${await getAccessToken()}`
						},
						signal: controller.signal
					}
				);
			} else {
				const response = await axios.post(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files?bucket=${bucket}&key=${encodeURIComponent(key)}&contentType=${encodeURIComponent(file.type || 'application/octet-stream')}`,
					{},
					{
						headers: {
							Authorization: `Bearer ${await getAccessToken()}`
						},
						signal: controller.signal
					}
				);

				const { uploadUrl } = response.data;
				await axios.put(uploadUrl, file, {
					headers: {
						'Content-Type': file.type || 'application/octet-stream'
					},
					signal: controller.signal
				});
			}

			await dbInsertionPromise;
			if (activeRecordId) {
				// Mark as completed and null out binary File blob to free RAM/disk space
				await updateUploadRecord(activeRecordId, {
					status: 'completed',
					file: null
				});

				if (activeBatchId) {
					const allBatchRecords = await getAllBucketUploads(bucket);
					const thisBatch = allBatchRecords.filter(
						(r) => r.batchId === activeBatchId
					);
					if (
						thisBatch.length > 0 &&
						thisBatch.every((r) => r.status === 'completed')
					) {
						await deleteBatchUploads(activeBatchId);
					}
				} else {
					await removeUploadRecord(activeRecordId);
				}
			}

			UploadManagerService.activeUploadsMap.delete(uploadKey);
			UploadManagerService.abortControllers.delete(uploadKey);
			UploadManagerService.activeRecordIds.delete(uploadKey);
			UploadManagerService.notifyListeners();

			if (options.onComplete) {
				options.onComplete();
			}
		} catch (error) {
			await dbInsertionPromise;
			if (activeRecordId && !controller.signal.aborted) {
				await updateUploadRecord(activeRecordId, { status: 'failed' });
			}

			UploadManagerService.activeUploadsMap.delete(uploadKey);
			UploadManagerService.abortControllers.delete(uploadKey);
			UploadManagerService.activeRecordIds.delete(uploadKey);
			UploadManagerService.notifyListeners();

			if (options.onError) {
				options.onError(error);
			}
			throw error;
		}
	}
}
